import { DataSource } from '@angular/cdk/table';
import type { Sort } from '@angular/material/sort';
import type { ActivatedRoute, Params, Router } from '@angular/router';
import type { ApiParams } from '@shared/http/api-params';
import type { Fetcher } from '@shared/http/fetcher';
import type { ApiResponseMeta } from '@shared/http/responses/api-response';
import type { ApiRequestStatus } from '@shared/models/api-data';
import { toApiObservable } from '@shared/models/api-state/operators';
import type { PaginatorApiParams } from '@shared/pagination/paginator';
import type { PresenterStatic } from '@shared/presenters/presenter.interface';
import { pick } from 'lodash-es';
import type { Subscription } from 'rxjs';
import { BehaviorSubject, combineLatest, ReplaySubject } from 'rxjs';
import type { Observable } from 'rxjs/internal/Observable';
import { Subject } from 'rxjs/internal/Subject';
import { debounceTime, distinctUntilChanged, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import type { AppSortParams } from '../models/app-sort-params.interface';
import { FilterDefinition } from './filters/filter.definition';

export interface DataSourcePagination {
    length: number;
    page: number;
    pageSize: number;
}

export class FetcherDataSource<T = any> extends DataSource<T> {
    public readonly refreshToken$ = new BehaviorSubject<void>(void 0);

    public readonly paginator$: BehaviorSubject<DataSourcePagination> = new BehaviorSubject<DataSourcePagination>({
        length: undefined,
        pageSize: 20,
        page: 1,
    });

    public get paginator(): DataSourcePagination {
        return this.paginator$.value;
    }

    private _queryParams$ = new BehaviorSubject<Params>({});

    public filterParams$: Observable<Params> = this._queryParams$.pipe(
        map(params => {
            if (this.allowedFilters) {
                return this.onlyAllowedFilters(params);
            }

            return params;
        }),
        distinctUntilChanged(),
        shareReplay(1),
    );

    public filterApiParams$ = this.filterParams$.pipe(
        map(params => this._buildFilterParams(params)),
    );

    private apiResponse$: Observable<ApiRequestStatus<T[]>> = combineLatest([
        this.filterApiParams$,
        this.refreshToken$,
    ]).pipe(
        tap(() => this.items$.next([])),
        switchMap(([filters]) => {
            const params = this._buildApiParams();

            const paginateConfig = {
                per_page: window.localStorage.getItem(this.paginationCacheKey()) || 10,
                ...params,
                ...filters,
            };

            if (params?.['all']) {
                delete paginateConfig['per_page'];
            }

            const observable = this.httpMethod === 'POST'
                ? this.fetcher.paginatePost(this.requestBody, paginateConfig)
                : this.fetcher.paginate(paginateConfig);

            return observable.pipe(
                toApiObservable(),
            );
        }),
        shareReplay(1),
    );

    public fetching$ = this.apiResponse$.pipe(map(response => response.fetching));

    private searchQuery$ = new Subject<string>();

    private _routeQueryParamsSubscription: Subscription;
    private _searchQuerySubscription: Subscription;

    public sortState: AppSortParams = {};

    public readonly items$ = new BehaviorSubject<T[]>([]);
    public readonly error$ = new BehaviorSubject<any>(null);
    public readonly meta$ = new ReplaySubject<ApiResponseMeta>();

    constructor(
        private fetcher: Fetcher,
        private activeRoute: ActivatedRoute = null,
        private router: Router = null,
        private apiParams: ApiParams | (() => ApiParams) = {},
        private allowedFilters: (string | FilterDefinition<any, any>)[] = [],
        private presenter?: PresenterStatic<any>,
        private httpMethod: 'GET' | 'POST' = 'GET',
        private requestBody: Record<string, unknown> = undefined,
    ) {
        super();

        if (this.requestBody && this.httpMethod !== 'POST') {
            throw new Error('requestBody can only be used with POST method');
        }
    }

    search(q: string) {
        if (q === null && (this._queryParams$.value.q === null || this._queryParams$.value.q === undefined)) {
            return;
        }

        this.searchQuery$.next(q);
    }

    refresh() {
        this.refreshToken$.next(undefined);
    }

    connect(): Observable<T[]> {
        this._searchQuerySubscription = this._searchQuerySubscription ?? this._subscribeSearchQuery();
        this._routeQueryParamsSubscription = this._routeQueryParamsSubscription ?? this._subscribeRouteQueryParams();

        return this.apiResponse$.pipe(
            tap(response => {
                if (response.successful) {
                    this.meta$.next(response.meta);

                    if (response.meta?.pagination) {
                        this.paginator$.next({
                            length: response.meta.pagination.total,
                            pageSize: response.meta.pagination.per_page,
                            page: response.meta.pagination.current_page - 1,
                        });
                    }

                    if (response.meta?.cursor) {
                        this.paginator$.next({
                            length: undefined,
                            pageSize: response.meta.cursor.count,
                            page: response.meta.cursor.current - 1,
                        });
                    }
                }
            }),
            map(response => {
                if (this.presenter) {
                    return response.transform(data => this.presenter.attachCollection(data));
                }

                return response;
            }),
            tap({
                next: response => this.error$.next(response.error ?? null),
                error: error => this.error$.next(error),
                complete: () => this.error$.next(null),
            }),
            map(response => response.data),
            tap({
                next: data => this.items$.next(data),
            }),
        );
    }

    disconnect() {
        this._searchQuerySubscription?.unsubscribe();
        this._routeQueryParamsSubscription?.unsubscribe();

        this._searchQuerySubscription = null;
        this._routeQueryParamsSubscription = null;
    }

    get current(): T[] {
        return this.items$.value ?? [];
    }

    patchSort(event: Partial<Sort>) {
        this.sort({
            active: this.sortState?.order_by,
            direction: this.sortState?.direction,
            ...event,
        });
    }

    sort(sortEvent: Sort) {
        // If no direction is provided clear both sorting query params to
        // avoid unintended empty queries
        const sortQuery: AppSortParams = sortEvent.direction && sortEvent.active ? {
            order_by: sortEvent.active,
            direction: sortEvent.direction,
        } : {
            order_by: undefined,
            direction: undefined,
        };

        // Set reset page number to 1 to show sorted data from beginning
        this.sortState = sortQuery;

        this._mergeParams({
            ...sortQuery,
            page: 1,
        });
    }

    paginate(pageEvent: { pageIndex: number }) {
        this._mergeParams({
            page: pageEvent.pageIndex + 1,
        });
    }

    changePageSize(pageSize: number) {
        window.localStorage.setItem(this.paginationCacheKey(), pageSize.toString());
        const pageParams: PaginatorApiParams = {
            per_page: pageSize,
            page: 1,
        };

        this._mergeParams(pageParams);
    }

    private paginationCacheKey() {
        const location = window.location.search ? window.location.href.split(window.location.search)[0] : window.location.href;
        const paginationKey = `${location}:${this.fetcher.modelName}`;
        return `pagination:${paginationKey}`;
    }

    private _subscribeSearchQuery(): Subscription {
        return this.searchQuery$
            .pipe(debounceTime(300))
            .subscribe(q => this._mergeParams({ q, page: 1 }));
    }

    private _subscribeRouteQueryParams(): Subscription {
        return this.activeRoute
            ?.queryParams
            ?.pipe(distinctUntilChanged())
            ?.subscribe(params => this._queryParams$.next(params));
    }

    private _buildApiParams(): ApiParams {
        const params = this.apiParams;

        if (typeof params === 'function') {
            return params();
        }

        return params;
    }

    private _buildFilterParams(params: Params): ApiParams {
        const apiParams = { ...params };

        Object.keys(apiParams).forEach(key => {
            const def = this.findFilterDefinition(key);
            let value = apiParams[key];

            if (def) {
                value = def.buildFilter(value).toApiParamValue();
            }

            if (value === undefined) {
                delete apiParams[key];
            }
        });

        // Add defaults
        const definitionsWithDefaults = this.filterDefinitionsWithDefaults();

        definitionsWithDefaults.forEach(definition => {
            if (apiParams.hasOwnProperty(definition.name)) {
                return;
            }

            apiParams[definition.name] = definition.defaultValue();
        });

        return apiParams;
    }

    private onlyAllowedFilters(params: Params) {
        const allowedFilters = [
            ...this.allowedFilters,
            'order_by',
            'direction',
            'page',
            'per_page',
            'q',
        ].map(f =>
            f instanceof FilterDefinition
                ? f.name
                : f,
        );

        return pick(
            params,
            allowedFilters,
        );
    }

    private filterDefinitions(): FilterDefinition<any, any>[] {
        const allowedFilters = [...this.allowedFilters];

        return allowedFilters.filter(
            (def): def is FilterDefinition<any, any> => def instanceof FilterDefinition,
        );
    }

    private findFilterDefinition(name: string): FilterDefinition<any, any> {
        return this.filterDefinitions().find(def => def.name === name);
    }

    private filterDefinitionsWithDefaults(): FilterDefinition<any, any>[] {
        return this.filterDefinitions().filter(def => def.hasDefaultValue);
    }

    private _mergeParams(pageParams: PaginatorApiParams): void {
        if (this.activeRoute) {
            this.router.navigate([], {
                queryParams: pageParams,
                queryParamsHandling: 'merge',
            });
        } else {
            this._queryParams$.next({
                ...this._queryParams$.value,
                ...pageParams,
            });
        }
    }
}

export function buildFetcherDataSource<T>(options: {
    fetcher: Fetcher;
    activeRoute?: ActivatedRoute;
    router?: Router;
    apiParams?: ApiParams | (() => ApiParams);
    allowedFilters?: (string | FilterDefinition<any, any>)[];
    presenter?: PresenterStatic<any>;
    httpMethod?: 'GET' | 'POST';
    requestBody?: Record<string, unknown>;
}): FetcherDataSource<T> {
    return new FetcherDataSource<T>(
        options.fetcher,
        options.activeRoute ?? undefined,
        options.router ?? undefined,
        options.apiParams ?? undefined,
        options.allowedFilters ?? undefined,
        options.presenter ?? undefined,
        options.httpMethod ?? 'GET',
        options.requestBody ?? undefined,
    );
}
